import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const BulkOrder = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const hasFetchedRef = useRef(null); // Store the combination of orderId+token that was fetched

  // Use environment variable or default to localhost for development
  // In production, this should be set to your backend URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || "https://flit-core-backend-production.up.railway.app/api";

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/rider/orders/${orderId}?token=${token}`
      );
      // Response format: {status: true, message: '...', data: {order: {...}}}
      // or {success: true, message: '...', data: {order: {...}}}
      const isSuccess = response.data.status === true || response.data.success === true;
      
      if (isSuccess && response.data.data) {
        // Handle both response formats: data.order or direct data
        const orderData = response.data.data.order || response.data.data;
        if (orderData) {
          setOrder(orderData);
        } else {
          setError(response.data.message || "Order data not found in response");
        }
      } else {
        setError(response.data.message || "Failed to fetch order details");
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch order details"
      );
    } finally {
      setLoading(false);
    }
  }, [orderId, token, API_BASE_URL]);

  useEffect(() => {
    if (orderId && token) {
      // Create a unique key for this orderId+token combination
      const fetchKey = `${orderId}-${token}`;
      
      // Only fetch if we haven't fetched for this specific combination
      if (hasFetchedRef.current !== fetchKey) {
        hasFetchedRef.current = fetchKey;
        fetchOrderDetails();
      }
    } else {
      setError("Order ID and token are required");
      setLoading(false);
    }
  }, [orderId, token, fetchOrderDetails]);

  const handleAction = async (action) => {
    if (actionLoading) return;

    try {
      setActionLoading(true);
      let response;

      switch (action) {
        case "arrived_pickup":
          response = await axios.post(
            `${API_BASE_URL}/rider/orders/${orderId}/arrived?token=${token}`
          );
          break;
        case "pickup":
          response = await axios.post(
            `${API_BASE_URL}/rider/orders/${orderId}/pickup?token=${token}`
          );
          break;
        case "arrived_drop":
          response = await axios.post(
            `${API_BASE_URL}/rider/orders/${orderId}/arrived-drop?token=${token}`
          );
          break;
        case "deliver":
          if (!photoFile) {
            alert("Please select a delivery photo");
            setActionLoading(false);
            return;
          }
          await handlePhotoUpload();
          return;
        default:
          setActionLoading(false);
          return;
      }

      // Check for both status and success fields
      const isSuccess = response.data.status === true || response.data.success === true;
      
      if (isSuccess) {
        // Refresh order details
        await fetchOrderDetails();
        alert("Action completed successfully!");
      } else {
        alert(response.data.message || "Action failed");
      }
    } catch (err) {
      console.error("Error performing action:", err);
      alert(
        err.response?.data?.message ||
          err.message ||
          "Failed to perform action"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handlePhotoUpload = async () => {
    try {
      setUploadingPhoto(true);

      // Upload photo and deliver order in one request to avoid CORS issues
      // Send file as FormData to backend which will handle S3 upload
      const formData = new FormData();
      formData.append('deliveryPhoto', photoFile);

      const deliverResponse = await axios.post(
        `${API_BASE_URL}/rider/orders/${orderId}/deliver?token=${token}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Check for both status and success fields
      const deliverSuccess = deliverResponse.data.status === true || deliverResponse.data.success === true;
      
      if (deliverSuccess) {
        await fetchOrderDetails();
        setPhotoFile(null);
        setPhotoPreview(null);
        alert("Order delivered successfully!");
      } else {
        throw new Error(deliverResponse.data.message || "Failed to deliver order");
      }
    } catch (err) {
      console.error("Error uploading photo:", err);
      alert(
        err.response?.data?.message ||
          err.message ||
          "Failed to upload photo and deliver order"
      );
    } finally {
      setUploadingPhoto(false);
      setActionLoading(false);
    }
  };

  const getFileExtension = (filename) => {
    return filename.split(".").pop().toLowerCase();
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getActionButton = () => {
    if (!order) return null;

    const status = order.status;

    switch (status) {
      case "accepted":
        return (
          <button
            onClick={() => handleAction("arrived_pickup")}
            disabled={actionLoading}
            className="bg-[#E5204B] text-white w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#c41a3f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? "Processing..." : "Arrived at Pickup"}
          </button>
        );

      case "arrived_pickup":
      case "pickup_verified":
        return (
          <button
            onClick={() => handleAction("pickup")}
            disabled={actionLoading}
            className="bg-[#E5204B] text-white w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#c41a3f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? "Processing..." : "Pick Up Order"}
          </button>
        );

      case "picked":
        return (
          <button
            onClick={() => handleAction("arrived_drop")}
            disabled={actionLoading}
            className="bg-[#E5204B] text-white w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#c41a3f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? "Processing..." : "Arrived at Destination"}
          </button>
        );

      case "arrived_drop":
      case "drop_verified":
        return (
          <div className="space-y-4 w-full">
            <div>
              <label className="block text-sm sm:text-base font-medium text-gray-700 mb-2">
                Upload Proof of Delivery (Photo)
              </label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                className="block w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-2 file:px-3 sm:file:px-4 file:rounded-lg file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-[#E5204B] file:text-white hover:file:bg-[#c41a3f]"
              />
              {photoPreview && (
                <div className="mt-4 flex justify-center">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="max-w-full sm:max-w-xs rounded-lg border border-gray-300"
                  />
                </div>
              )}
            </div>
            <button
              onClick={() => handleAction("deliver")}
              disabled={actionLoading || uploadingPhoto || !photoFile}
              className="bg-[#E5204B] text-white w-full px-6 sm:px-8 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#c41a3f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadingPhoto
                ? "Uploading..."
                : actionLoading
                ? "Processing..."
                : "Deliver Order"}
            </button>
          </div>
        );

      case "delivered":
        return (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg w-full">
            <p className="font-semibold text-base sm:text-lg">
              ✓ Order Delivered Successfully!
            </p>
            <p className="mt-2 text-sm sm:text-base">This order has been completed.</p>
            {order.deliveryPhoto && (
              <div className="mt-4 flex justify-center">
                <img
                  src={order.deliveryPhoto}
                  alt="Delivery proof"
                  className="max-w-full sm:max-w-md rounded-lg border border-green-300"
                />
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-gray-600">
            Current Status: <span className="font-semibold">{status}</span>
          </div>
        );
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      accepted: "bg-blue-100 text-blue-800",
      arrived_pickup: "bg-yellow-100 text-yellow-800",
      pickup_verified: "bg-yellow-100 text-yellow-800",
      picked: "bg-purple-100 text-purple-800",
      arrived_drop: "bg-orange-100 text-orange-800",
      drop_verified: "bg-orange-100 text-orange-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold ${
          statusColors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status.replace(/_/g, " ").toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[#E5204B] mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-gray-600">
            Loading order details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-4xl sm:text-5xl mb-4">⚠️</div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Error</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 break-words">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#E5204B] text-white px-6 py-2 rounded-lg font-semibold text-sm sm:text-base hover:bg-[#c41a3f] transition-colors w-full sm:w-auto"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center text-sm sm:text-base text-gray-600">
          Order not found
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
                Bulk Order Details
              </h1>
              <p className="text-sm sm:text-base text-gray-600 break-all">
                Order ID: {order.orderId || order.id}
              </p>
              {order.meta?.packageId && (
                <p className="text-sm sm:text-base text-gray-600 break-all mt-1">
                  Package ID: {order.meta.packageId}
                </p>
              )}
            </div>
            <div className="flex-shrink-0">
              {getStatusBadge(order.status)}
            </div>
          </div>
        </div>

        {/* Order Details */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
            Order Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Pickup Details */}
            <div className="border border-gray-200 rounded-lg p-4 sm:p-5">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                <span className="mr-2 text-xl sm:text-2xl">📥</span> Pickup Details
              </h3>
              <div className="space-y-2 text-sm sm:text-base text-gray-600">
                <p className="break-words">
                  <span className="font-semibold">Location:</span> Hub (All packages)
                </p>
                {order.pickupLocation?.address && (
                  <p className="break-words">
                    <span className="font-semibold">Address:</span>{" "}
                    {order.pickupLocation.address}
                  </p>
                )}
                {order.pickupLocation?.name && (
                  <p>
                    <span className="font-semibold">Contact Person:</span>{" "}
                    {order.pickupLocation.name}
                  </p>
                )}
                {order.pickupLocation?.phone && (
                  <p>
                    <span className="font-semibold">Phone:</span>{" "}
                    <a
                      href={`tel:${order.pickupLocation.phone}`}
                      className="text-[#E5204B] hover:underline"
                    >
                      {order.pickupLocation.phone}
                    </a>
                  </p>
                )}
                {order.pickupLocation?.latitude && order.pickupLocation?.longitude && (
                  <div className="mt-3">
                    <a
                      href={`https://www.google.com/maps?q=${order.pickupLocation.latitude},${order.pickupLocation.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#E5204B] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-[#c41a3f] transition-colors"
                    >
                      <span>📍</span> Open in Google Maps
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Drop Details */}
            <div className="border border-gray-200 rounded-lg p-4 sm:p-5">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                <span className="mr-2 text-xl sm:text-2xl">📤</span> Delivery Details
              </h3>
              <div className="space-y-2 text-sm sm:text-base text-gray-600">
                {order.dropLocation?.address && (
                  <p className="break-words">
                    <span className="font-semibold">Address:</span>{" "}
                    {order.dropLocation.address}
                  </p>
                )}
                {order.dropLocation?.name && (
                  <p>
                    <span className="font-semibold">Contact Person:</span>{" "}
                    {order.dropLocation.name}
                  </p>
                )}
                {order.dropLocation?.phone && (
                  <p>
                    <span className="font-semibold">Phone:</span>{" "}
                    <a
                      href={`tel:${order.dropLocation.phone}`}
                      className="text-[#E5204B] hover:underline"
                    >
                      {order.dropLocation.phone}
                    </a>
                  </p>
                )}
                {order.dropLocation?.latitude && order.dropLocation?.longitude && (
                  <div className="mt-3">
                    <a
                      href={`https://www.google.com/maps?q=${order.dropLocation.latitude},${order.dropLocation.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#E5204B] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-[#c41a3f] transition-colors"
                    >
                      <span>📍</span> Open in Google Maps
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Package Details */}
          <div className="mt-4 sm:mt-6 border border-gray-200 rounded-lg p-4 sm:p-5">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
              <span className="mr-2 text-xl sm:text-2xl">📦</span> Package Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm sm:text-base text-gray-600">
              <div>
                <span className="font-semibold">Weight:</span>{" "}
                {order.weight?.value || "N/A"} {order.weight?.unit || "kg"}
              </div>
              <div>
                <span className="font-semibold">Distance:</span>{" "}
                {order.distance?.value?.toFixed(2) || "N/A"}{" "}
                {order.distance?.unit || "km"}
              </div>
              <div>
                <span className="font-semibold">Estimated Time:</span>{" "}
                {order.estimatedTime?.value || "N/A"}{" "}
                {order.estimatedTime?.unit || "minutes"}
              </div>
            </div>
            {order.itemDescription && (
              <div className="mt-3 sm:mt-4">
                <span className="font-semibold text-gray-800 text-sm sm:text-base">
                  Description:
                </span>{" "}
                <span className="text-gray-600 text-sm sm:text-base break-words">
                  {order.itemDescription}
                </span>
              </div>
            )}
          </div>

          {/* Estimated Time/Distance to Location */}
          {order.estimatedTimeToLocation && (
            <div className="mt-4 sm:mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
              <h3 className="text-base sm:text-lg font-semibold text-blue-800 mb-2">
                Estimated Time to Location
              </h3>
              <p className="text-sm sm:text-base text-blue-600">
                {order.estimatedTimeToLocation.value}{" "}
                {order.estimatedTimeToLocation.unit}
              </p>
              {order.estimatedDistanceToLocation && (
                <p className="text-sm sm:text-base text-blue-600 mt-1">
                  Distance: {order.estimatedDistanceToLocation.value}{" "}
                  {order.estimatedDistanceToLocation.unit}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
            Order Actions
          </h2>
          <div className="flex justify-center">
            {getActionButton()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOrder;

